import { DateFormat } from '@startuptools/ui/DateFormat';
import { Button } from '@startuptools/ui/Button';
import { Progress, ProgressStep } from '@startuptools/ui/Progress';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import {
  agreementStatusToProgress,
  AgreementStatusExplanation,
  DecisionStatusExplanation,
  decisionStatusToProgress,
  registrationStatusToProgress,
  RegistrationStatusExplanation,
} from './utils';
import { type GqlInvestment, GqlRegistrationStatus } from '../../../../graphql/base-types.graphql';
import { DashboardTable, EmptyState } from './Table';
import { useNavigate } from '../../../../components/Navigate';
import { Link } from 'react-router-dom';

export const InvestmentTable = ({
  investments,
}: {
  investments: Pick<
    GqlInvestment,
    'id' | 'kind' | 'name' | 'lastSubDate' | 'decisionStatus' | 'agreementStatus' | 'registrationStatus' | 'description'
  >[];
}) => {
  const { i18n } = useLingui();

  const { navigate, link } = useNavigate();
  const hasInvestments = investments.length > 0;

  return (
    <DashboardTable
      title={<Trans id="Investments" />}
      emptyState={
        <EmptyState
          header={
            <Trans id="You haven't created any investment yet. Get started by creating your first one - digital signing is included with all paid plans." />
          }
          cta={
            <Button size="sm" component={Link} to={link('/investments/share-issues')}>
              <Trans id="Create share issue" />
            </Button>
          }
        />
      }
      rows={investments.map(investment => (
        <tr
          key={investment.id}
          onClick={() => {
            if (investment.kind === 'share-issue') {
              void navigate(`/investments/share-issues/${investment.id}/show/summary`);
            }
            if (investment.kind === 'wise') {
              void navigate(`/investments/wise-options/${investment.id}/edit/main-step-program`);
            }
          }}
        >
          <td>
            <Stack gap={1}>
              <Typography level="body-md" noWrap>
                {investment.name ?? investment.description}
              </Typography>
              {investment.name && (
                <Typography level="body-sm" textColor="neutral.500" noWrap>
                  {investment.description}
                </Typography>
              )}
            </Stack>
          </td>
          <td>
            <DateFormat locale={i18n.locale}>{investment.lastSubDate}</DateFormat>
          </td>
          <td>
            <Progress>
              <ProgressStep
                status={decisionStatusToProgress(investment.decisionStatus)}
                label={<Trans id="Decision" />}
                tooltipText={<DecisionStatusExplanation status={investment.decisionStatus} />}
              />

              <ProgressStep
                status={agreementStatusToProgress(investment.agreementStatus)}
                label={investment.kind === 'share-issue' ? <Trans id="Subscription" /> : <Trans id="Agreement" />}
                tooltipText={<AgreementStatusExplanation status={investment.agreementStatus} />}
              />
              {investment.registrationStatus !== GqlRegistrationStatus.NotApplicable && (
                <ProgressStep
                  status={registrationStatusToProgress(investment.registrationStatus)}
                  tooltipText={<RegistrationStatusExplanation status={investment.registrationStatus} />}
                  label={<Trans id="Registration" />}
                />
              )}
            </Progress>
          </td>
        </tr>
      ))}
      hasItems={hasInvestments}
    />
  );
};
